/** ***********
*
* <map.service.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  NG6C740 [thomas.pereira.external@airbus.com]
*  ng6c740 [thomas.pereira.external@airbus.com]
*  ydjoudad [yzaid.djoudad@soprasteria.com]
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { EsriBeacon } from './../models/beacon';
import { WorkerService } from 'src/app/services/workers.service';
import { ElementRef, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { EsriMapService, MapConfiguration } from './esri-map/esri-map.service';
import { selectMapAssetsFilters$, selectMapAssetFilterLoaded$, selectAdminZoneFilters$, selectZoneFilterLoaded$ } from '../store/selectors/filter.selectors';
import { selectBeaconLayer$, selectBeaconLayerLoaded$ } from '../store/selectors/beacon-layer.selectors';
import { Asset } from '../models/asset';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Zone } from '../models/zone';
import { AssetService } from './asset.service';
import { successAssetHistory } from '../store/actions/asset-history.actions';
import { Event } from '../models/event';
import { ActivatedRoute } from '@angular/router';
import { Beacon } from '../models/beacon';
import { ConfigurationStorageKeys, LocalStorageService } from './local-storage.service';
import { ErrorTranslationService } from './error-translation.service';
import { TranslateService } from '@ngx-translate/core';
import { selectTrackerLoaded$ } from '../store/selectors/tracker.selectors';
import { fetchBeaconLayer } from '../store/actions/beacon-layer.action';
import { Anchor } from '../models/anchor';
import { selectAnchor$, selectAnchorsLoaded$ } from '../store/selectors/anchor.selectors';
import { fetchAllAnchors } from '../store/actions/anchor.action';
import { OutdatedHorizonService } from './outdated-horizon.service';
import { IndoorBuilding, IndoorBuildingOption, IndoorFloorOption, IndoorSiteOption } from 'src/app/models/indoor-map';


@Injectable({
    providedIn: 'root'
})
export class MapService {
    private _assets$: Observable<Asset[]>;
    private _assets: Asset[];
    private _zones$: Observable<Zone[]>;
    private _beacons$: Observable<EsriBeacon[]>;
    private _anchors$: Observable<Anchor[]>;
    private _isWebMapLoaded = false;
    private _mapConfiguration: MapConfiguration = {};
    /**
     * Emit true when data (assets, trackers and so on) are loaded and reduced in the store
     * Based on tracker loaded state as it is the laste object te be set to loaded
     */
    private _appDataLoaded$: Observable<boolean>;

    public isWebMapLoaded$ = new BehaviorSubject<boolean>(this._isWebMapLoaded);
    public isRealTime: boolean;
    public isHistoryMode$ = new BehaviorSubject<boolean>(false);
    public isMenuVisible$ = new BehaviorSubject<boolean>(true);
    public historyRangeType$ = new BehaviorSubject<string>('');
    public unselectedItem$: BehaviorSubject<boolean>;
    public selectedAsset$: BehaviorSubject<Asset>;
    public selectedZone$: BehaviorSubject<Zone>;
    public selectedCluster$: BehaviorSubject<Asset[]>;
    public selectedBeacon$: BehaviorSubject<any>;
    public selectedBeaconCluster$: BehaviorSubject<Beacon[]>;
    public selectedAnchor$: BehaviorSubject<any>;
    public selectedAnchorCluster$: BehaviorSubject<Anchor[]>;
    public selectedAssetHistory$: BehaviorSubject<any>;
    public selectedHistoryCluster$: BehaviorSubject<any>;
    public isZonesDisplayed$: BehaviorSubject<boolean>;
    public indoorSites$ = this._mapFactoryService.indoorSiteOptions$.asObservable();
    public indoorBuildings$ = this._mapFactoryService.indoorBuildingOptions$.asObservable();
    public indoorFloors$ = this._mapFactoryService.indoorFloorOptions$.asObservable();
    public indoorLoading$ = this._mapFactoryService.indoorLoading$.asObservable();
    public indoorSitesLoadError$ = this._mapFactoryService.indoorSitesLoadError$.asObservable();
    public indoorSiteBuildingsLoading$ = this._mapFactoryService.indoorSiteBuildingsLoading$.asObservable();
    public indoorFloorDetailsLoading$ = this._mapFactoryService.indoorFloorDetailsLoading$.asObservable();
    public indoorBuildingsDetailsLoading$ = this._mapFactoryService.indoorBuildingsDetailsLoading$.asObservable();
    public selectedIndoorSiteId$ = this._mapFactoryService.selectedIndoorSiteId$.asObservable();
    public selectedIndoorBuildingId$ = this._mapFactoryService.selectedIndoorBuildingId$.asObservable();
    public selectedIndoorFloorId$ = this._mapFactoryService.selectedIndoorFloorId$.asObservable();
    public selectedIndoorBuilding$ = this._mapFactoryService.selectedIndoorBuilding$.asObservable();
    public zones: Zone[];
    public selectedDisabledMomentStatus$ = new BehaviorSubject<boolean>(false);
    public disabledOutdatedHorizon$ = new BehaviorSubject<boolean>(false);
    public mapDisplayed$ = new BehaviorSubject<boolean>(false);
    public beaconsDisplayed$ = new BehaviorSubject<boolean>(false);
    public anchorsDisplayed$ = new BehaviorSubject<boolean>(false);
    public indoorDisplayed$ = new BehaviorSubject<boolean>(false);
    public assetLabelsDisplayed$ = new BehaviorSubject<boolean>(false);
    /**
     * The constructor prepare the observables providing the data in order to avoid multiple call to the same webMap function
     */
    constructor(
        private _mapFactoryService: EsriMapService,
        private store: Store,
        private _assetService: AssetService,
        private _workersService: WorkerService,
        private _errorTranslationService: ErrorTranslationService,
        private _translate: TranslateService,
        private _localStorageService: LocalStorageService,
        private _route: ActivatedRoute,
        private _outdatedHorizonService: OutdatedHorizonService,
    ) {
        this._assets$ = this.store.select(selectMapAssetFilterLoaded$).pipe(
            filter(assetsLoaded => assetsLoaded),
            take(1),
            switchMap(() => this.store.select(selectMapAssetsFilters$)),
            // workerMap(this._workersService.formatAssets, (assets) => ({ assets }), ({ assets }: { assets: Asset[] }) => assets),
        );

        this._zones$ = this.store.select(selectZoneFilterLoaded$).pipe(
            filter(loaded => loaded),
            take(1),
            switchMap(() => this.store.select(selectAdminZoneFilters$))
        );

        this._beacons$ = this.store.select(selectBeaconLayerLoaded$).pipe(
            filter(loaded => loaded),
            take(1),
            switchMap(() => this.store.select(selectBeaconLayer$))
        );

        this._anchors$ = this.store.select(selectAnchorsLoaded$).pipe(
            filter(loaded => loaded),
            take(1),
            switchMap(() => this.store.select(selectAnchor$))
        );

        this._appDataLoaded$ = this.store.select(selectTrackerLoaded$);

        this.unselectedItem$ = this._mapFactoryService.unselectedItem$;
        this.selectedAsset$ = this._mapFactoryService.selectedAsset$;
        this.selectedCluster$ = this._mapFactoryService.selectedCluster$;
        this.selectedBeacon$ = this._mapFactoryService.selectedBeacon$;
        this.selectedBeaconCluster$ = this._mapFactoryService.selectedBeaconCluster$;
        this.selectedAnchor$ = this._mapFactoryService.selectedAnchor$;
        this.selectedAnchorCluster$ = this._mapFactoryService.selectedAnchorCluster$;
        this.selectedAssetHistory$ = this._mapFactoryService.selectedAssetHistory$;
        this.selectedHistoryCluster$ = this._mapFactoryService.selectedHistoryCluster$;
        this.selectedZone$ = this._mapFactoryService.selectedZone$;
        this.isZonesDisplayed$ = new BehaviorSubject<boolean>(false);
        this._setMapConfigurationFromLocalStorage();
    }

    /**
     * Set the map initial configuration from the local storage
     */
    private _setMapConfigurationFromLocalStorage() {
        this._localStorageService.storageValue$.pipe(
            filter(({ key }) => [
                ConfigurationStorageKeys.displaySatellite,
                ConfigurationStorageKeys.displayBeacons,
                ConfigurationStorageKeys.displayAnchors,
                ConfigurationStorageKeys.displayIndoor,
                ConfigurationStorageKeys.displayZone,
                ConfigurationStorageKeys.displayAssetLabels,
                ConfigurationStorageKeys.disabledMovementStatus,
                ConfigurationStorageKeys.disabledOutdatedHorizon,
            ].includes(key as ConfigurationStorageKeys)),
            map((entry) => ({ ...entry, value: JSON.parse(entry.value) })),
        ).pipe(
            distinctUntilChanged(),
        ).subscribe(
            ({ key, value }) => {
                switch (key) {
                    case ConfigurationStorageKeys.displayBeacons:
                        this._mapConfiguration.displayBeacons = value;
                        this.beaconsDisplayed$.next(value);
                        if (value) {
                            this.store.dispatch(fetchBeaconLayer());
                        }
                        break;
                    case ConfigurationStorageKeys.displayAnchors:
                        this._mapConfiguration.displayAnchors = value;
                        this.anchorsDisplayed$.next(value);
                        if (value) {
                            this.store.dispatch(fetchAllAnchors());
                        }
                        break;
                    case ConfigurationStorageKeys.displayIndoor:
                        this._mapConfiguration.displayIndoor = value;
                        this.indoorDisplayed$.next(value);
                        break;
                    case ConfigurationStorageKeys.displayZone:
                        this._mapConfiguration.displayZone = value;
                        this.isZonesDisplayed$.next(value);
                        break;
                    case ConfigurationStorageKeys.displayAssetLabels:
                        this._mapConfiguration.displayAssetLabels = value;
                        this.assetLabelsDisplayed$.next(value);
                        break;
                    case ConfigurationStorageKeys.displaySatellite:
                        this._mapConfiguration.mapType = value ? 'satellite' : 'openStreetMap';
                        this.mapDisplayed$.next(value);
                        break;
                    case ConfigurationStorageKeys.disabledMovementStatus:
                        this._mapConfiguration.disabledMovementStatus = value;
                        this.selectedDisabledMomentStatus$.next(value);
                        break;
                    case ConfigurationStorageKeys.disabledOutdatedHorizon:
                        this._mapConfiguration.disabledOutdatedHorizon = value;
                        this.disabledOutdatedHorizon$.next(value);
                        break;
                    default:
                        break;
                }
            }
        );
        this._localStorageService.fetchLocalStorageValues$().subscribe();
    }

    /**
     * Init the map and subscribe to data
     */
    /**
 * Initialize the map and subscribe to application data.
 *
 * An Outdated Horizon API failure must not prevent
 * the Esri map from loading.
 *
 * @param elementRef Map container reference.
 * @return Map initialization promise.
 */
public async initMap(
    elementRef: ElementRef,
): Promise<void> {
    await this._mapFactoryService.loadWebMap(
        elementRef,
        this._appDataLoaded$,
        this._mapConfiguration,
    );

    await this._subscribeDataAndDisplayLayers();

    this._isWebMapLoaded = true;
    this.isWebMapLoaded$.next(
        this._isWebMapLoaded,
    );
}

    /**
     * Re render the layers when data source changes and when the user zooms
     */
    private async _subscribeDataAndDisplayLayers() {
        this._assets$.subscribe(async (assets) => {
            this._assets = assets;
            await this.displayAssets(assets);
            this._refreshOnZoom();
        });

        this._zones$.subscribe(async (zones) => {
            this.zones = zones;
            await this.displayZones(zones);
        });

        this._beacons$.subscribe(async (beacons) => {
            await this.displayBeacons(beacons);
        });

        this._anchors$.subscribe(async (anchors) => {
            await this.displayAnchors(anchors);
        });

        // zone visibility
        if (Object.keys(this._route.snapshot.queryParams).some(param => param.startsWith('zone'))) {
            this.setZonesVisibility(true);
        }
    }

    /**
     * Display assets pin points and clusters
     */
    public async displayAssets(assets: Asset[]) {
        await this._mapFactoryService.displayAssets(assets);
    }

    /**
     * Zoom and center on displayed assets
     * @param assets optional, if not provided, assets are retrieved from the store
     */
    public zoomAndCenterOnAssets(assets?: Asset[]) {
        this.onceWebMapIsLoaded().subscribe(() => {
            if (!assets) {
                this._assets$.pipe(take(1)).subscribe((_assets) => {
                    this._mapFactoryService.centerAndZoomOnAssets(_assets);
                });
            } else {
                this._mapFactoryService.centerAndZoomOnAssets(assets);
            }
        });
    }

    /**
     * Show the asset card
     */
    public async showAssetPopin(asset: Asset) {
        this.isWebMapLoaded$.pipe(
            filter(v => v),
            take(1),
        ).subscribe(
            () => this._mapFactoryService.showPopinOnGraphicLayer(asset)
        );
    }

    /**
     * Display zones
     */
    public async displayZones(zones: Zone[]) {
        await this._mapFactoryService.displayZones(zones);
    }

    /**
     * Change the visibility of zones
     */
    public setZonesVisibility(isVisible: boolean) {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.setZonesVisibility(isVisible);
            this.isZonesDisplayed$.next(isVisible);
        });
    }

    public setIndoorVisibility(isVisible: boolean) {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.setIndoorLayerVisibility(isVisible);
            this.indoorDisplayed$.next(isVisible);
        });
    }

    public getIndoorSites(): IndoorSiteOption[] {
        return this._mapFactoryService.getIndoorSiteOptions();
    }

    public getIndoorBuildings(): IndoorBuildingOption[] {
        return this._mapFactoryService.getIndoorBuildingOptions();
    }

    public getIndoorFloors(): IndoorFloorOption[] {
        return this._mapFactoryService.getIndoorFloorOptions();
    }

    public getSelectedIndoorSiteId(): string | null {
        return this._mapFactoryService.getSelectedIndoorSiteId();
    }

    public getSelectedIndoorBuildingId(): string | null {
        return this._mapFactoryService.getSelectedIndoorBuildingId();
    }

    public getSelectedIndoorFloorId(): string | null {
        return this._mapFactoryService.getSelectedIndoorFloorId();
    }

    public getSelectedIndoorBuilding(): IndoorBuilding | null {
        return this._mapFactoryService.getSelectedIndoorBuilding();
    }

    public setIndoorSite(siteId: string) {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.setIndoorSite(siteId);
        });
    }

    public setIndoorBuilding(buildingId: string) {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.setIndoorBuilding(buildingId);
        });
    }

    public setIndoorFloor(floorId: string) {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.setIndoorFloor(floorId);
        });
    }

    public clearIndoorSelections() {
        this.onceWebMapIsLoaded().subscribe(() => {
            this._mapFactoryService.clearIndoorSelections();
        });
    }

    public setDisabledMovementStatus(isMovementStatusDisabled: boolean) {
        this.selectedDisabledMomentStatus$.next(isMovementStatusDisabled);
        this._mapFactoryService.setDisabledMovementStatus(this._assets, isMovementStatusDisabled);
    }

    /**
 * Enable or disable outdated horizon styling.
 *
 * @param {boolean} disabledOutdatedHorizon
 * True to disable outdated marker styling.
 * @return {void}
 */
    public setDisabledOutdatedHorizon(
    disabledOutdatedHorizon: boolean,): void {this.disabledOutdatedHorizon$.next(disabledOutdatedHorizon,);
    this.onceWebMapIsLoaded().subscribe((): void => {void this._mapFactoryService.setDisabledOutdatedHorizon(this._assets ?? [],disabledOutdatedHorizon,
         );
        });
    }
    /**
     * Zoom and center on zones if they are displayed
     */
    public zoomOnZones(zones?: Zone[]) {
        this.onceWebMapIsLoaded().subscribe(() => {
            if (!zones) {
                this._zones$.pipe(take(1)).subscribe((_zones) => {
                    this._mapFactoryService.zoomAndCenterOnZones(_zones);
                });
            } else {
                this._mapFactoryService.zoomAndCenterOnZones(zones);
            }
        });
    }

    /**
     * Display beacons
     */
    public async displayBeacons(beacons: EsriBeacon[]) {
        await this._mapFactoryService.displayBeacons(beacons);
    }

    /**
  * Display anchors
  */
    public async displayAnchors(anchor: Anchor[]) {
        await this._mapFactoryService.displayAnchors(anchor);
    }
    /**
     * Change the visibility of beacons
     */
    public async switchMap(isPressed: boolean) {
        await this._mapFactoryService.switchMap(isPressed);
        await this._subscribeDataAndDisplayLayers();
        this.mapDisplayed$.next(isPressed);
    }
    /**
     * Change the visibility of beacons and its labels
     */
    public setBeaconsVisibility(isVisible: boolean) {
        this._mapFactoryService.setBeaconsVisibility(isVisible);
        this._mapFactoryService.setBeaconLabelsVisibility(isVisible);
        this.beaconsDisplayed$.next(isVisible);
    }

    /**
     * Change the visibility of anchors and its labels
     */
    public setAnchorsVisibility(isVisible: boolean) {
        this._mapFactoryService.setAnchorsVisibility(isVisible);
        this._mapFactoryService.setAnchorLabelsVisibility(isVisible);
        this.anchorsDisplayed$.next(isVisible);
    }

    /**
     * Display history
     */
    public displayHistory(asset: Asset, startDate: string, endDate: string, rangeType: string) {
        this.isWebMapLoaded$.next(false);
        return this._assetService.getAssetHistory(asset.family.id, asset.id, startDate, endDate).pipe(
            catchError((error) => {
                this.isWebMapLoaded$.next(true);
                const msgCode = this._errorTranslationService.getMessageCode(error);
                switch (msgCode) {
                    case `TRACKER_NOT_ASSOCIATED_WITH_ASSET`:
                        this._errorTranslationService.openSnackBarErrorWithParams('TRACKER_NOT_ASSOCIATED_WITH_ASSET', { assets: asset.name });
                        break;
                    default:
                        this._errorTranslationService.handleError(error, this._translate.instant('INTERNAL_ERROR'));
                        break;
                }
                return throwError(error);
            }),
            map(assetHistory => assetHistory.map((history) => {
                history.source = history.source?.toLowerCase().replace('geobeacon', 'beacon');
                history.state = history.state?.toLowerCase().replace('on_the_move', 'otm');
                return history;
            })),
            tap(async (assetHistory) => {
                // dispatch needed for MapHistoryComponent
                this.store.dispatch(successAssetHistory({ payload: assetHistory, zones: this.zones }));
                this.isWebMapLoaded$.next(true);
                if (!assetHistory.length) {
                    return;
                }
                await this._mapFactoryService.displayHistory(assetHistory, asset, true);
                this.isHistoryMode$.next(true);
                this.historyRangeType$.next(rangeType);
            }));

    }

    /**
     * Close history and display assets
     */
    public async closeHistory() {
        // close the history
        this._mapFactoryService.closeHistory();
        this.isHistoryMode$.next(false);
        // display assets
        this._assets$.pipe(take(1)).subscribe(async (assets) => {
            await this.displayAssets(assets);
        });
    }

    /**
     * zoom, center on history event and display the history popin
     */
    public showHistoryPopin(history: Event) {
        // center on selected history event and show popin
        this._mapFactoryService.showPopinOnHistoryGraphicLayer(history);
    }

    /**
     * Clear the layers corresponding to a selected asset (i.e popin, asset card)
     * @param bool whether the last selected asset should be cleared
     */
    public resetSelectedItem(bool: boolean) {
        this._mapFactoryService.resetSelectedItem(bool);
    }

    /**
     * Subscribe on zoom-end event in order to refresh clusters and pinpoint layers
     */
    private _refreshOnZoom() {
        this._mapFactoryService.refreshLayers(this._assets, this._beacons$, this._anchors$);
    }

    /**
     * Informs when the webmap is loaded
     * @returns Observable which emit one time once the web map is loaded, no need to unsubscribe
     */
    public onceWebMapIsLoaded(): Observable<boolean> {
        return this.isWebMapLoaded$.pipe(
            filter(loaded => loaded),
            take(1)
        );
    }

    /**
     * Change the visibility of the labels of the assets
     */
    public setAssetLabelsVisibility(isVisible: boolean): void {
        this._mapFactoryService.setAssetLabelsVisibility(isVisible);
        this.assetLabelsDisplayed$.next(isVisible);
    }

    /**
 * Zoom and center on displayed assets
 * @param assets optional, if not provided, assets are retrieved from the store
 */
    public zoomAndCenterOnAnchors(anchors?: Anchor[]) {
        this.onceWebMapIsLoaded().subscribe(() => {
            if (!anchors) {
                this._anchors$.pipe(take(1)).subscribe((_anchors) => {
                    this._mapFactoryService.centerAndZoomOnAnchors(_anchors);
                });
            } else {
                this._mapFactoryService.centerAndZoomOnAnchors(anchors);
            }
        });
    }

    public zoomAndCenterOnBeacons(beacons?: EsriBeacon[]) {
        this.onceWebMapIsLoaded().subscribe(() => {
            if (!beacons) {
                this._beacons$.pipe(take(1)).subscribe((_beacons) => {
                    this._mapFactoryService.centerAndZoomOnBeacons(_beacons);
                });
            } else {
                this._mapFactoryService.centerAndZoomOnBeacons(beacons);
            }
        });
    }

}
